import { FmlxButton, FmlxDateRangePicker, FmlxIcon, FmlxTooltip } from 'fmlx-common-ui';
import React, { useMemo, useRef, useState } from 'react';
import './NodeDetailView.scss';

type NodeDetailViewDateFilterProps = {
  className?: string;
  startDate?: string;
  endDate?: string;
  onDateRangeChange?: (start, end) => void;
};

export const NodeDetailViewDateFilter = (props: NodeDetailViewDateFilterProps) => {
  const { className, startDate, endDate, onDateRangeChange } = props;
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const dateRangePickerRef = useRef(null);

  const onFilterButtonClick = () => {
    if (hasDate) {
      onClearButtonClick();
    } else {
      onCalendarButtonClick();
    }
  };

  const onCalendarButtonClick = () => {
    // Can only be clicked when the date is empty
    if (!startDate && !endDate) {
      const datePicker = dateRangePickerRef.current?.querySelector('button');
      datePicker?.click();
    }
  };

  const onClearButtonClick = () => {
    handleDateRangeChange(undefined, undefined);
  };

  const formatToDDMMYYYY = (dateStr: string): string => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleDateRangeChange = (start, end) => {
    // handle undefined start or end but not both (both undefined is only possible if we clear the date using button)
    if (start || end) {
      if (!start) {
        start = startDate;
      } else if (!end) {
        end = endDate;
      }
    }
    onDateRangeChange(start, end);
    setIsDialogOpen(false); // for redundancy
  };

  const dateText = useMemo<string>(() => {
    return startDate && endDate
      ? `${formatToDDMMYYYY(startDate)} - ${formatToDDMMYYYY(endDate)}`
      : '';
  }, [startDate, endDate]);

  const hasDate = startDate || endDate;

  return (
    <div className={className}>
      <div
        className={`node-detail-view-date-filter-input-div ${
          isDialogOpen ? 'is-open' : ''
        }`}
      >
        <FmlxTooltip title={dateText} placement={FmlxTooltip.Placement.BOTTOM}>
          <div className="absolute m-1 left-0 right-[22px] text-xs font-normal truncate">
            {dateText}
          </div>
        </FmlxTooltip>
        <div className="absolute right-0 h-[23px] w-[23px] date-filter-button">
          <FmlxButton
            className="h-[22px] w-[22px]"
            variant={FmlxButton.Variant.PLAIN}
            type={FmlxButton.Type.BASIC}
            icon={<FmlxIcon name={hasDate ? 'Cancel' : 'Calendar'} />}
            onlyIcon
            onClick={onFilterButtonClick}
            tooltipDescription={hasDate ? 'Clear Filter' : 'Add Date Range Filter'}
            tooltipPlacement={FmlxButton.TooltipPlacement.BOTTOM}
          />
        </div>
      </div>
      <div ref={dateRangePickerRef} className={'date-filter-container'}>
        <FmlxDateRangePicker
          labelPosition={FmlxDateRangePicker.LabelPosition.LEFT}
          labelStartDate={''}
          labelEndDate={''}
          onChange={handleDateRangeChange}
          startValue={startDate}
          endValue={endDate}
          onOpenDatePicker={() => setIsDialogOpen(true)}
          onCloseDatePicker={() => setIsDialogOpen(false)}
        />
      </div>
    </div>
  );
};
